import type { AppState } from '@protector/entities/__legacy__/redux.types';
import { Entitlements } from '@protector/entities/licensing/constants';
import type { UUID } from '@protector/utils/types';
import { CROPWISE_URL } from 'config/constants';
import { COLOR_OPTIONS } from 'config/style';
import { validatePlansAndEntitlements } from 'core/utils/functions';
import type { TFunction } from 'i18next';
import { useGetRBACAuthorityUser } from 'querys/rbac/rbac.query';
import { RBACActions, RBACLevels, RBACPermissionTypes } from '@protector/entities/rbac/constants';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import NewPlusIcon from 'st-protector-icons/Glyph/NewPlusIcon';
import SvgLeaf from 'st-protector-icons/Minor/Leaf';
import { SvgNemaDigital } from 'st-protector-icons/Minor/NemaDigital';
import type { STOrgSettingsCardProps } from '../components/settings/org-settings-card.component';
import { useFarmSettingsOptions } from './use-farm-setting-options.hook';

const transformProtectorSettings = (settings: STOrgSettingsCardProps[], available: boolean) => {
  if (settings?.length && available) {
    return settings.map(setting => ({
      key: setting.title,
      label: setting.title,
      tooltipText: setting.description,
      icon: setting.svgIcon,
      onClick: setting.onClick
    }));
  }

  return [];
};

const createOrgDeepAction = (workspaceId: UUID | undefined, callback: string, t: TFunction) => ({
  key: 'createOrg',
  label: t('modals.organization_settings.create_org.title'),
  tooltipText: t('modals.organization_settings.create_org.description'),
  icon: <NewPlusIcon fill={COLOR_OPTIONS.BLUE_60} />,
  link: `${CROPWISE_URL}/deep-actions/organization?workspace_id=${workspaceId}&callback_uri=${callback}
  `
});

const createPropertyDeepAction = (organizationId: UUID | undefined, callback: string, t: TFunction) => ({
  key: 'createProperty',
  label: t('modals.organization_settings.create_property.title'),
  tooltipText: t('modals.organization_settings.create_property.description'),
  icon: <NewPlusIcon fill={COLOR_OPTIONS.BLUE_60} />,
  link: `${CROPWISE_URL}/deep-actions/create-property?org_id=${organizationId}&callback_uri=${callback}
  `
});

const configureMultiPropertiesAction = (t: TFunction, onClick: () => void) => ({
  key: 'configureMultiProperties',
  label: t('modals.organization_settings.configure_multi_properties.title'),
  tooltipText: t('modals.organization_settings.configure_multi_properties.description'),
  icon: <SvgNemaDigital fill={COLOR_OPTIONS.BLUE_60} />,
  onClick
});

interface UseFarmSettingsProps {
  workspaceId?: string;
  orgId?: string;
}

export const useFarmSettingsMenuProps = ({ workspaceId, orgId }: UseFarmSettingsProps) => {
  const { host } = globalThis.location;
  const [t] = useTranslation();
  const navigate = useNavigate();
  const licensingStatus = useSelector((state: AppState) => state.entities.company.licensingStatus);
  const { orgSettingsButtonAvailable, orgSettingsContentOptions, propertySettingsAvailable, propertySettingsContentOptions } =
    useFarmSettingsOptions();
  const organizationPermission = useGetRBACAuthorityUser({
    rbacActions: RBACActions.ORGANIZATION,
    rbacLevels: RBACLevels.ORG,
    rbacPermission: [RBACPermissionTypes.WRITE]
  });

  const propertyPermission = useGetRBACAuthorityUser({
    rbacActions: RBACActions.PROPERTY,
    rbacLevels: RBACLevels.ORG,
    rbacPermission: [RBACPermissionTypes.WRITE]
  });

  const hasNemadigitalPermission = useSelector((state: AppState) => state.uiState.auth.permissionFlags?.nemaDigital?.value);

  const handleGoToNemadigital = useCallback(() => {
    const nemadigitalUrl = `/company/${orgId}/nemadigital`;
    navigate(nemadigitalUrl);
    globalThis.dispatchEvent(new CustomEvent('CW-CLOSE-SETTINGS-MENU'));
  }, [orgId, navigate]);

  const transformedOrgSettings = transformProtectorSettings(
    orgSettingsContentOptions,
    orgSettingsButtonAvailable || propertySettingsAvailable
  );
  const transformedPropertySettings = transformProtectorSettings(propertySettingsContentOptions, propertySettingsAvailable);

  const hasOrganizationDeepLink = useMemo(
    () => (organizationPermission?.length ? [createOrgDeepAction(workspaceId, host, t)] : []),
    [host, organizationPermission?.length, t, workspaceId]
  );

  const hasPropertyDeepLink = useMemo(
    () => (propertyPermission?.length && orgId ? [createPropertyDeepAction(orgId, host, t)] : []),
    [host, orgId, propertyPermission?.length, t]
  );

  const deepActions = useMemo(
    () =>
      validatePlansAndEntitlements(licensingStatus, null, [Entitlements.CREATE_ORG_AND_PROPERTY])
        ? [...hasOrganizationDeepLink, ...hasPropertyDeepLink]
        : [],
    [licensingStatus, hasOrganizationDeepLink, hasPropertyDeepLink]
  );

  const nemadigitalMultiConfig = useMemo(
    () => (hasNemadigitalPermission && orgId ? [configureMultiPropertiesAction(t, handleGoToNemadigital)] : []),
    [hasNemadigitalPermission, orgId, t, handleGoToNemadigital]
  );

  const parsedPropertySettings = useMemo(
    () => [...transformedOrgSettings, ...transformedPropertySettings, ...deepActions, ...nemadigitalMultiConfig],
    [transformedOrgSettings, transformedPropertySettings, deepActions, nemadigitalMultiConfig]
  );

  const productSettings = useMemo(() => {
    if (!parsedPropertySettings.length) {
      return null;
    }
    return {
      key: 'protector',
      title: t('modals.organization_settings.product_name.protector'),
      icon: <SvgLeaf />,
      tooltipText: '',
      items: parsedPropertySettings
    };
  }, [parsedPropertySettings, t]);

  return productSettings;
};
